import type { DealTeamIntegrationLike, DealTeamRoleMapping } from './types'

export function isConfiguredRoleMapping(value: unknown): value is DealTeamRoleMapping {
	return (
		typeof value === 'object' &&
		value !== null &&
		typeof (value as DealTeamRoleMapping).crmRoleValue === 'string' &&
		typeof (value as DealTeamRoleMapping).roleId === 'string' &&
		typeof (value as DealTeamRoleMapping).formId === 'string'
	)
}

export const getTimeAgo = (date: Date) => {
	const now = new Date()
	const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

	if (seconds < 60) return 'Just now'
	const minutes = Math.floor(seconds / 60)
	if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const getStatusBadge = (status: string) => {
	const config: Record<string, { bg: string; text: string; label: string }> = {
		active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
		paused: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Paused' },
		pending_setup: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Pending' },
		error: { bg: 'bg-red-100', text: 'text-red-700', label: 'Error' },
	}
	const c = config[status] || config.pending_setup
	return <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>{c.label}</span>
}

export const getSourceIcon = (provider: string) => {
	if (provider === 'webhook') {
		return (
			<div className='flex items-center gap-1.5 text-neutral-600'>
				<svg
					className='w-4 h-4'
					fill='none'
					viewBox='0 0 24 24'
					stroke='currentColor'
					strokeWidth={1.5}
					aria-hidden='true'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						d='M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.07-9.07l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757'
					/>
				</svg>
				<span className='text-sm'>Webhook</span>
			</div>
		)
	}
	return (
		<div className='flex items-center gap-1.5 text-neutral-600'>
			<svg
				className='w-4 h-4'
				fill='none'
				viewBox='0 0 24 24'
				stroke='currentColor'
				strokeWidth={1.5}
				aria-hidden='true'>
				<path
					strokeLinecap='round'
					strokeLinejoin='round'
					d='M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z'
				/>
			</svg>
			<span className='text-sm'>CRM</span>
		</div>
	)
}

export const getLastSyncDisplay = (integration: DealTeamIntegrationLike) => {
	const syncDate = integration.lastSyncAt
	const errorDate = integration.lastErrorAt

	if (integration.status === 'error' && errorDate) {
		const timeAgo = getTimeAgo(new Date(errorDate))
		return (
			<div className='flex items-center gap-1.5 text-red-500'>
				<svg
					className='w-4 h-4'
					fill='none'
					viewBox='0 0 24 24'
					stroke='currentColor'
					strokeWidth={1.5}
					aria-hidden='true'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
					/>
				</svg>
				<span className='text-sm'>{timeAgo}</span>
			</div>
		)
	}

	if (syncDate) {
		const timeAgo = getTimeAgo(new Date(syncDate))
		return (
			<div className='flex items-center gap-1.5 text-neutral-500'>
				<svg
					className='w-4 h-4'
					fill='none'
					viewBox='0 0 24 24'
					stroke='currentColor'
					strokeWidth={1.5}
					aria-hidden='true'>
					<path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' />
				</svg>
				<span className='text-sm'>{timeAgo}</span>
			</div>
		)
	}

	return (
		<div className='flex items-center gap-1.5 text-neutral-400'>
			<svg
				className='w-4 h-4'
				fill='none'
				viewBox='0 0 24 24'
				stroke='currentColor'
				strokeWidth={1.5}
				aria-hidden='true'>
				<path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' />
			</svg>
			<span className='text-sm'>Not yet</span>
		</div>
	)
}
